import { useMemo } from 'react';
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core';
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common';
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en';

// Initialize zxcvbn options once
zxcvbnOptions.setOptions({
  translations: zxcvbnEnPackage.translations,
  graphs: zxcvbnCommonPackage.adjacencyGraphs,
  dictionary: {
    ...zxcvbnCommonPackage.dictionary,
    ...zxcvbnEnPackage.dictionary,
  },
});

const SCORE_LABELS = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'] as const;
const SCORE_COLORS = [
  'bg-destructive',
  'bg-destructive',
  'bg-yellow-500',
  'bg-green-500',
  'bg-green-600',
] as const;

export const MIN_PASSWORD_SCORE = 3;

interface PasswordStrengthResult {
  score: number;
  label: string;
  warning: string;
  suggestions: string[];
  isStrong: boolean;
}

export function evaluatePassword(
  password: string,
  userInputs: string[] = []
): PasswordStrengthResult {
  if (!password) {
    return { score: 0, label: '', warning: '', suggestions: [], isStrong: false };
  }
  const result = zxcvbn(password, userInputs);
  return {
    score: result.score,
    label: SCORE_LABELS[result.score],
    warning: result.feedback.warning ?? '',
    suggestions: result.feedback.suggestions ?? [],
    isStrong: result.score >= MIN_PASSWORD_SCORE,
  };
}

interface PasswordStrengthMeterProps {
  password: string;
  userInputs?: string[];
}

export function PasswordStrengthMeter({
  password,
  userInputs = [],
}: PasswordStrengthMeterProps): React.ReactElement | null {
  const strength = useMemo(() => evaluatePassword(password, userInputs), [password, userInputs]);

  if (!password) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              i <= strength.score ? SCORE_COLORS[strength.score] : 'bg-muted'
            }`}
          />
        ))}
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{strength.label}</p>
        {password.length >= 12 && !strength.isStrong && (
          <p className="text-xs text-destructive">Stronger password required</p>
        )}
      </div>
      {strength.warning && <p className="text-xs text-destructive">{strength.warning}</p>}
      {strength.suggestions.length > 0 && (
        <p className="text-xs text-muted-foreground">{strength.suggestions[0]}</p>
      )}
    </div>
  );
}
